import type { ReactNode } from 'react';
import styles from './AppShell.module.css';

/**
 * **`AppShell`**（§5.4）—
 * ヘッダ（ブランド + `QuotaChip`）/ 履歴サイドバー / 本体 / 理解状態パネル。
 *
 * ## 会話が主、パネルが脇（§4.8）
 *
 * Atlassian Rovo の `sidebar chat`（会話を脇に置く）を**逆にする** —
 * **この製品では会話そのものが成果物**なので脇に寄せられない。
 *
 * ## 狭幅でもパネルを消さない
 *
 * D11 は「**全 Requirement を名前付きで**見せる」と定めており、
 * **幅が無いことは見せない理由にならない**。**本文の下に積む。**
 * （履歴レールは消してよい — **D12 の再開はレール以外の入口からもできる**）
 *
 * ## ランドマークを付ける
 *
 * **`<header>` / `<main>` / `<aside>`** で領域を分ける。
 * **付けないと、読み上げの利用者は毎回先頭から辿ることになる。**
 */
export interface AppShellProps {
  /** レールの頭に置くブランド（モック: `.rail .brandline`） */
  readonly brand: ReactNode;
  /**
   * **ブランドのシンボル**（原本 SVG の文字列。D120 決定2）。
   *
   * **`Icon` に載せない** — Xlabo のマークは**色を持つ絵**であり、
   * §3.4 の「**アイコンに独自の色を割り当てない**（`currentColor` を継ぐ）」は
   * **線画の規約**である。**ブランドの色は継がせずそのまま出す。**
   */
  readonly brandMark: string;
  /** **スレッドのバー**に出す問題名（1 行で切る） */
  readonly title: string;
  /** バーの右側（残メッセージ数・自分の情報・新しい問題） */
  readonly barRight: ReactNode;
  readonly rail: ReactNode;
  readonly panel: ReactNode;
  /** 会話のログ */
  readonly children: ReactNode;
  /** **下に固定する入力欄**（会話が伸びても動かない） */
  readonly composer: ReactNode;
}

export function AppShell({
  brand,
  brandMark,
  title,
  barRight,
  rail,
  panel,
  children,
  composer,
}: AppShellProps): ReactNode {
  return (
    <div className={styles['shell']}>
      <aside className={styles['rail']} aria-label="これまでの問題">
        <div className={styles['brand']}>
          <span
            className={styles['brandMark']}
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: brandMark }}
          />
          {brand}
        </div>
        {rail}
      </aside>
      <main className={styles['main']}>
        {/**
         * **スレッドのバー**（モック）。**全幅のヘッダーにしない** —
         * **全幅は「アプリの枠」に見える**が、**この製品では会話が主**である。
         */}
        <div className={styles['bar']}>
          <span className={styles['barTitle']}>{title}</span>
          {barRight}
        </div>
        {/**
         * **ログだけがスクロールする** — 全体を 1 つにすると入力欄が画面外へ出る。
         * **中身は `logInner` で中央に寄せる** — 広い画面で左端に貼りつかせない
         * （M16。実機で「**余白がおかしいから画面左がはみ出てる**」と読まれた）。
         */}
        <div className={styles['log']}>
          <div className={styles['logInner']}>{children}</div>
        </div>
        <div className={styles['composerArea']}>
          {/** **ログと同じ行長にする** — 揃えないと発話と入力の左端がずれる */}
          <div className={styles['composerInner']}>{composer}</div>
        </div>
      </main>
      <aside className={styles['panel']}>{panel}</aside>
    </div>
  );
}
